import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import verificationCodeService from '../../utils/verificationCodeService';
import emailService from '../../services/emailService';

import { useAuth } from '../../contexts/AuthContext';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Authentication steps: 'credentials' -> 'verification' -> 'success'
  const [authStep, setAuthStep] = useState('credentials');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    verificationCode: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [canResendCode, setCanResendCode] = useState(false);

  // Admin credentials from environment variables
  const ADMIN_EMAIL = import.meta.env?.VITE_ADMIN_EMAIL || 'bizznet.app@gmail.com';
  const ADMIN_PASSWORD = import.meta.env?.VITE_ADMIN_PASSWORD || 'Admin@Bizznet2025';

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/admin-panel');
    }
  }, [user, navigate]);

  // Timer for code expiration
  useEffect(() => {
    let interval;
    if (authStep === 'verification' && remainingTime > 0) {
      interval = setInterval(() => {
        const timeLeft = verificationCodeService?.getRemainingTime(formData?.email);
        setRemainingTime(timeLeft);
        
        if (timeLeft === 0) {
          setCanResendCode(true);
        }
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [authStep, remainingTime, formData?.email]);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateCredentials = () => {
    const newErrors = {};

    // Exact email validation
    if (!formData?.email) {
      newErrors.email = 'El correo electrónico es requerido';
    } else if (formData?.email !== ADMIN_EMAIL) {
      newErrors.email = 'Correo electrónico no autorizado para acceso administrativo';
    }

    // Exact password validation
    if (!formData?.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData?.password !== ADMIN_PASSWORD) {
      newErrors.password = 'Contraseña incorrecta';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleCredentialsSubmit = async (e) => {
    e?.preventDefault();

    if (!validateCredentials()) {
      return;
    }

    setIsLoading(true);

    try {
      // Send verification code via Resend
      const result = await emailService?.sendAdmin2FAEmail(formData?.email);
      
      if (result?.success) {
        setAuthStep('verification');
        setRemainingTime(300); // 5 minutes
        setCanResendCode(false);
        setErrors({});
      } else {
        setErrors({ submit: result?.message || 'Error al enviar código de verificación' });
      }
    } catch (error) {
      setErrors({ submit: 'Error al procesar la solicitud. Intenta nuevamente.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationSubmit = async (e) => {
    e?.preventDefault();

    if (!formData?.verificationCode) {
      setErrors({ verificationCode: 'Ingresa el código de verificación' });
      return;
    }

    if (formData?.verificationCode?.length !== 6) {
      setErrors({ verificationCode: 'El código debe tener 6 dígitos' });
      return;
    }

    setIsLoading(true);

    try {
      // Validate verification code
      const result = emailService?.validateVerificationCode(formData?.email, formData?.verificationCode);
      
      if (result?.valid) {
        // Successful authentication
        setAuthStep('success');
        
        // Navigate to admin panel after brief success message
        setTimeout(() => {
          navigate('/admin-panel');
        }, 1500);
      } else {
        setErrors({ verificationCode: result?.message });
      }
    } catch (error) {
      setErrors({ verificationCode: 'Error al validar el código. Intenta nuevamente.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    setErrors({});

    try {
      const result = await emailService?.sendAdmin2FAEmail(formData?.email);
      
      if (result?.success) {
        setRemainingTime(300); // Reset to 5 minutes
        setCanResendCode(false);
        setFormData(prev => ({ ...prev, verificationCode: '' }));
      } else {
        setErrors({ submit: result?.message });
      }
    } catch (error) {
      setErrors({ submit: 'Error al reenviar código. Intenta nuevamente.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToCredentials = () => {
    emailService?.clearVerificationCode(formData?.email);
    setAuthStep('credentials');
    setFormData(prev => ({ ...prev, verificationCode: '' }));
    setErrors({});
    setRemainingTime(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link 
              to="/login-selection" 
              className="flex items-center gap-2 text-gray-600 hover:text-purple-600 transition-colors"
            >
              <Icon name="ArrowLeft" size={20} />
              <span className="font-medium">Volver a Selección</span>
            </Link>
            
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                <Icon name="Shield" size={20} color="#FFFFFF" />
              </div>
              <span className="text-xl font-bold text-gray-900">BIZZNET Admin</span>
            </div>
          </div>
        </div>
      </header>
      {/* Main content */}
      <main className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-600 mb-8">
          <Link to="/landing-page" className="hover:text-purple-600">Inicio</Link>
          <Icon name="ChevronRight" size={16} />
          <Link to="/login-selection" className="hover:text-purple-600">Selección</Link>
          <Icon name="ChevronRight" size={16} />
          <span className="text-purple-600 font-medium">Login Administrador</span>
        </nav>

        {/* Login card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          {/* Card header */}
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-8 text-white text-center">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon 
                name={authStep === 'success' ? 'CheckCircle' : authStep === 'verification' ? 'ShieldCheck' : 'Shield'} 
                size={40} 
                color="#FFFFFF" 
              />
            </div>
            <h1 className="text-2xl font-bold mb-2">
              {authStep === 'success' ? 'Acceso Autorizado' : 
               authStep === 'verification' ? 'Verificación de Seguridad' : 
               'Acceso Administrativo'}
            </h1>
            <p className="text-purple-100">
              {authStep === 'success' ? 'Redirigiendo al panel de administración...' :
               authStep === 'verification' ? 'Ingresa el código enviado a tu correo' :
               'Ingresa tus credenciales de administrador'}
            </p>
          </div>

          {/* Card body */}
          <div className="p-8">
            {/* Step 1: Credentials */}
            {authStep === 'credentials' && (
              <form onSubmit={handleCredentialsSubmit} className="space-y-6">
                {/* Email field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Correo Electrónico Autorizado
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Icon name="Mail" size={20} className="text-gray-400" />
                    </div>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData?.email}
                      onChange={handleChange}
                      placeholder="bizznet.app@gmail.com"
                      className={`pl-10 ${errors?.email ? 'border-red-500' : ''}`}
                    />
                  </div>
                  {errors?.email && (
                    <p className="mt-1 text-sm text-red-600">{errors?.email}</p>
                  )}
                </div>

                {/* Password field */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Contraseña
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Icon name="Lock" size={20} className="text-gray-400" />
                    </div>
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData?.password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      className={`pl-10 pr-10 ${errors?.password ? 'border-red-500' : ''}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <Icon 
                        name={showPassword ? 'EyeOff' : 'Eye'} 
                        size={20} 
                        className="text-gray-400 hover:text-gray-600" 
                      />
                    </button>
                  </div>
                  {errors?.password && (
                    <p className="mt-1 text-sm text-red-600">{errors?.password}</p>
                  )}
                </div>

                {/* Remember me & Forgot password */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e?.target?.checked)}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Recordarme</span>
                  </label>
                  <Link 
                    to="/password-recovery-request" 
                    className="text-sm text-purple-600 hover:text-purple-700 hover:underline"
                  >
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>

                {/* Submit error */}
                {errors?.submit && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                    <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-600">{errors?.submit}</p>
                  </div>
                )}

                {/* Submit button */}
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 rounded-xl transition-colors duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Icon name="Loader2" size={20} className="animate-spin" />
                      <span>Verificando credenciales...</span>
                    </>
                  ) : (
                    <>
                      <Icon name="ShieldCheck" size={20} />
                      <span>Continuar a Verificación</span>
                    </>
                  )}
                </Button>
              </form>
            )}

            {/* Step 2: Verification Code */}
            {authStep === 'verification' && (
              <form onSubmit={handleVerificationSubmit} className="space-y-6">
                {/* Info message */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="Mail" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-semibold text-blue-900 mb-1">
                        Código Enviado
                      </h3>
                      <p className="text-xs text-blue-800">
                        Hemos enviado un código de verificación de 6 dígitos a <strong>{formData?.email}</strong>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Verification code field */}
                <div>
                  <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 mb-2">
                    Código de Verificación
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Icon name="Key" size={20} className="text-gray-400" />
                    </div>
                    <Input
                      id="verificationCode"
                      name="verificationCode"
                      type="text"
                      maxLength={6}
                      value={formData?.verificationCode}
                      onChange={handleChange}
                      placeholder="000000"
                      className={`pl-10 text-center text-2xl tracking-widest font-mono ${errors?.verificationCode ? 'border-red-500' : ''}`}
                    />
                  </div>
                  {errors?.verificationCode && (
                    <p className="mt-1 text-sm text-red-600">{errors?.verificationCode}</p>
                  )}
                </div>

                {/* Timer */}
                {remainingTime > 0 && (
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                    <Icon name="Clock" size={16} />
                    <span>El código expira en: <strong className="text-purple-600">{formatTime(remainingTime)}</strong></span>
                  </div>
                )}

                {/* Submit button */}
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 rounded-xl transition-colors duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Icon name="Loader2" size={20} className="animate-spin" />
                      <span>Verificando código...</span>
                    </>
                  ) : (
                    <>
                      <Icon name="LogIn" size={20} />
                      <span>Verificar e Ingresar</span>
                    </>
                  )}
                </Button>

                {/* Resend code */}
                <div className="text-center">
                  {canResendCode ? (
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={isLoading}
                      className="text-sm text-purple-600 hover:text-purple-700 hover:underline font-medium"
                    >
                      Reenviar código
                    </button>
                  ) : (
                    <p className="text-sm text-gray-500">
                      ¿No recibiste el código? Podrás reenviarlo cuando expire el tiempo.
                    </p>
                  )}
                </div>

                {/* Back button */}
                <button
                  type="button"
                  onClick={handleBackToCredentials}
                  className="w-full text-sm text-gray-600 hover:text-gray-800 flex items-center justify-center gap-2"
                >
                  <Icon name="ArrowLeft" size={16} />
                  <span>Volver a credenciales</span>
                </button>
              </form>
            )}

            {/* Step 3: Success */}
            {authStep === 'success' && (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="CheckCircle" size={40} className="text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  Autenticación Exitosa
                </h2>
                <p className="text-gray-600 mb-6">
                  Acceso autorizado. Redirigiendo al panel de administración...
                </p>
                <div className="flex items-center justify-center gap-2">
                  <Icon name="Loader2" size={20} className="text-purple-600 animate-spin" />
                  <span className="text-sm text-gray-600">Cargando panel...</span>
                </div>
              </div>
            )}

            {/* Security notice - Only show in credentials step */}
            {authStep === 'credentials' && (
              <div className="mt-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="ShieldAlert" size={20} className="text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-purple-900 mb-1">
                      Acceso Restringido con Verificación en Dos Pasos
                    </h3>
                    <p className="text-xs text-purple-800">
                      Este panel requiere autenticación de múltiples factores. Solo el correo autorizado puede acceder. 
                      Todos los intentos son monitoreados y registrados.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Help section */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600 mb-2">
            ¿Problemas para acceder?
          </p>
          <a 
            href="mailto:bizznet.latam@gmail.com" 
            className="text-sm text-purple-600 hover:text-purple-700 hover:underline font-medium"
          >
            Contactar Soporte Técnico
          </a>
        </div>
      </main>
      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-sm text-gray-600">
            © 2025 BIZZNET. Todos los derechos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AdminLogin;